import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type { BffHealth, WsStatus } from '../useBffPing';
import { buildPing, parseHello } from '../bff';
import { LinkHealthLedger } from './link-health';
import { logCall, logWs, rumAction } from '@/lib/rum';
import { getFreshWsUrl, invalidateWsToken } from '@/lib/ws-url';
import { backoffDelayMs } from '@/lib/backoff';
import { shouldEmit } from './presence-beat';
import { redirectToLogin } from '@/lib/auth-client';
import { isAuthGenuinelyLost } from '@/lib/auth-refusal';
import { cockpitReducer, initialState, type CockpitState } from './state';
import {
  endCallFrame,
  joinPoolFrame,
  leavePoolFrame,
  abandonLeaveFrame,
  listPoolsFrame,
  presenceHelloFrame,
  presenceBeatFrame,
  presenceAckFrame,
  callManuallyDroppedFrame,
  parseCockpitFrame,
  refreshVoiceTokenFrame,
  resyncCallFrame,
  clientErrorFrame,
  voicemailPromptRaisedFrame,
  silenceGateFrame,
  type CockpitClientFrame,
  type CockpitServerFrame,
  type Disposition,
} from './protocol';

// Compact, payload-free summaries for the WS logs: the frame kind + key ids only
// (never contact name/phone or the auth token) — enough to trace the realtime
// flow in Datadog without dumping payloads.
function recvSummary(frame: CockpitServerFrame): Record<string, unknown> {
  const s: Record<string, unknown> = { kind: frame.kind };
  switch (frame.kind) {
    case 'pools':
      s.count = frame.pools.length;
      break;
    case 'voice_token':
      s.minted = frame.voice.token !== null;
      break;
    case 'pool_joined':
    case 'pool_left':
      s.poolId = frame.poolId;
      break;
    case 'pool_leaving':
      s.poolId = frame.poolId;
      s.pendingCalls = frame.pendingCalls;
      break;
    case 'lead_assigned':
      s.callId = frame.call.callId;
      s.poolId = frame.call.poolId;
      break;
    case 'talk_track':
      s.callId = frame.callId;
      break;
    case 'lead_calendar':
      s.callId = frame.callId;
      break;
    case 'call_ended':
      s.callId = frame.callId;
      s.disposition = frame.disposition;
      break;
    case 'session_restored':
      s.poolId = frame.poolId;
      s.hasCall = frame.currentCall !== null;
      break;
    case 'reconnect':
      s.reason = frame.reason;
      break;
  }
  return s;
}

function sendSummary(frame: CockpitClientFrame): Record<string, unknown> {
  const s: Record<string, unknown> = { kind: frame.kind };
  if ('poolId' in frame) s.poolId = frame.poolId;
  if ('callId' in frame) s.callId = frame.callId;
  if ('disposition' in frame) s.disposition = frame.disposition;
  if ('action' in frame) s.action = frame.action;
  return s;
}

// =====================================================================
// useCockpit — owns the single BFF WebSocket for the calling flow and bridges
// it to the pure cockpit reducer (state.ts):
//   - inbound server frames are parsed and dispatched as reducer actions;
//   - user actions optimistically dispatch (to block the UI on a round-trip)
//     AND send the matching client frame.
// Reconnects with a jittered, capped backoff, minting a fresh auth token per attempt.
// The reducer ignores out-of-phase frames, so a stray late frame is harmless.
// =====================================================================

// Which control the SDR reached for. Recorded on the telemetry only, so the two
// duplicated button rows and the keyboard can be told apart when judging whether the
// shortcuts are used and whether the wrong one gets pressed. 'gate' = the silence
// prompt's own Voicemail/End call shortcuts (and the countdown-expiry auto-cut).
export type HangupSource = 'key' | 'top' | 'bottom' | 'gate';

// A silence-gate receipt on its way to the BFF — the silence_gate frame minus the
// addressing, which reportSilenceGate stamps from the live call.
export type SilenceGateReport = Omit<
  Extract<CockpitClientFrame, { kind: 'silence_gate' }>,
  'kind' | 'callId'
>;

export interface CockpitController {
  state: CockpitState;
  wsStatus: WsStatus;
  // Link health measured on THIS socket — the one carrying calls — from its own
  // ping/pong stream (plans/single-websocket/plan.md). Same shape and windows as
  // useBffPing so the control box can read either.
  linkHealth: BffHealth;
  // The BFF's hello said this connection runs single-websocket: skip the health-
  // ping socket and show linkHealth on the chip. Server-decided per connection.
  // Tri-state ON PURPOSE: `null` = no hello yet, and the probe socket must not
  // open on a guess — a flag-ON SDR's mount would otherwise still create the
  // redundant socket (plus its BFF session + Voice-token mint) for one round-trip.
  // An explicit `false` (including a legacy hello with no config) opens the probe;
  // a rollback BFF therefore flips reconnecting tabs back automatically.
  singleWs: boolean | null;
  // Whether the in-call email check can answer. No "not yet known" state, unlike
  // singleWs: until a hello says otherwise the control is disabled.
  emailCheck: boolean;
  // Report that somebody is at this tab. Coalesced, so callers may fire it freely.
  reportActivity: () => void;
  // The SDR answered the "still there?" prompt.
  answerPresence: () => void;
  selectPool: (poolId: string) => void;
  endCall: (source: HangupSource) => void;
  leavePool: () => void;
  // Give up on a drain that never finished and return to the pool picker.
  abandonLeave: () => void;
  // Ask the BFF to mint + push a fresh Voice token (on SDK tokenWillExpire).
  refreshVoiceToken: () => void;
  // The silent-voicemail banner was raised — report it to the BFF as telemetry. The
  // call stays live; the SDR ends it via markVoicemail if it really is a voicemail.
  reportVoicemailPromptRaised: () => void;
  // A silence-gate receipt (raised/stayed/cut) — telemetry only; the `cut` hangup
  // itself goes through endCall('gate') at the call site (routes/cockpit.tsx).
  reportSilenceGate: (event: SilenceGateReport) => void;
  // The softphone hit a device/call error — report it so a browser fault is attributable
  // instead of reading as the far end hanging up. Attribution only; the leg is already gone.
  reportClientError: (code: number | null) => void;
  // The SDR manually flagged a voicemail AMD missed: stop the live call and tell
  // the BFF (counted separately as an AMD miss; same return-to-pool path).
  markVoicemail: (source: HangupSource) => void;
  // Audio is live but the card never arrived — ask the BFF to re-push it from the core
  // snapshot. Referentially stable (see impl) so the cockpit.tsx reconcile interval
  // isn't torn down by unrelated re-renders.
  resyncCall: () => void;
}

const RECONNECT_MS = 2000;

// On a deploy `reconnect` nudge, wait a random 0–RECONNECT_JITTER_MS before opening the
// new socket, so N nudged clients don't stampede the single new task all at once. The
// rate that matters is fleet ÷ this window, not the window itself.
// Spent from the same hold the restore deadline runs under: this wait plus a whole
// reconnect round-trip has to fit inside HOLD_CEILING_MS - RESTORE_TIMEOUT_MS, or the
// unluckiest draw gets a truncated restore window and fewer retries against a task that
// is not accepting yet.
export const RECONNECT_JITTER_MS = 5000;

// Migration-wide deadline (armed when the nudge fires): how long to keep retrying the
// reconnect before giving up and dropping to the picker (the new task genuinely has no
// session for us). Generous — the restore is a sub-second core snapshot fetch — so a
// live call is only ever torn down by this timeout under a pathologically slow new task.
const RESTORE_TIMEOUT_MS = 15_000;

// Backstop on the re-armable RESTORE_TIMEOUT_MS, which a flapping BFF would otherwise
// push back forever. Under the core's ~30s reap: past that the leg is gone anyway.
const HOLD_CEILING_MS = 25_000;

// After `session_restored` arrives, keep the migration guard up for this long before
// declaring it done. With lb_cookie stickiness the task that answered the reconnect may
// be the still-DRAINING old one, which closes the socket within its shutdown grace
// (~RECONNECT_GRACE_MS on the BFF); this window must exceed that grace so such a close
// is caught as "keep migrating" rather than clearing the guard and dropping the call.
const MIGRATION_SETTLE_MS = 3500;

// One ping stream, two jobs (plans/single-websocket/plan.md):
// (a) KEEPALIVE — traffic so the socket never idles out. Must stay comfortably
//     under the SHORTEST idle timeout on the browser→BFF path: CloudFront's
//     `/bff/*` behavior sets no originReadTimeout, so its 30s default is the
//     tightest layer (ALB is 300s). An idle-reaped socket mid-call is destructive
//     on both ends (card cleared + non-graceful deregister → core hangs up).
// (b) MEASUREMENT — each pong feeds the in-band link-health ledger. 2s is the
//     exact cadence the ping-socket chip samples today (useBffPing's default), so
//     the derived miss (3×) and stale (2.5×) windows — and the dot's
//     responsiveness — are unchanged by the single-websocket consolidation.
// Changing this constant changes BOTH; don't split them back apart.
const PING_MS = 2_000;

// Uncapped 2s retries hammered the BFF at ~0.5/s indefinitely once a token expired.
const RECONNECT_MAX_MS = 30_000;

// Tighter while a call is held: a 30s retry would let the hold expire before we
// tried again.
const RECONNECT_HOLD_MAX_MS = 3_000;

// How long the deploying banner may stand without being re-asserted. Sized to
// outlast a normal roll with margin: the pause window runs single-digit
// minutes, and a card, reconnect or restore clears the banner sooner anyway.
const DEPLOYING_BANNER_TTL_MS = 5 * 60_000;

/**
 * Should this close hold the call and keep migrating, rather than end it?
 *
 * `ws_closed` clears the card, which hangs up the Twilio leg — so a transient drop
 * mid-conversation must NOT dispatch it. Held closes fall through to the restore
 * deadline, which drops to the picker only if nothing recovers.
 */
export function shouldHoldCall(args: {
  code: number;
  hasLiveCall: boolean;
  reconnecting: boolean;
}): boolean {
  if (args.reconnecting) return false; // the existing migration guard owns it
  // An idle cockpit should show `closed` at once, not sit in a false "reconnecting".
  if (!args.hasLiveCall) return false;
  // Only 1000 is deliberate. A client-OBSERVED 1000 means the server closed normally
  // (or our own detached reconnect close, whose handler is superseded-guarded) — the
  // user's own navigation/tab-close never delivers a close event to the departing
  // page at all. 1001 is the BFF's own shutdown close (hub.closeAll) — normally
  // preceded by a `reconnect` nudge, but a client that missed it (a frozen/bfcache'd
  // page, or the frame lost in the shutdown grace window) sees a bare 1001 mid-call,
  // and treating it as deliberate hangs up a live conversation via the phase-keyed
  // Twilio effect.
  return args.code !== 1000;
}

// The seat restores on the first reconnect or not at all — don't leave an idle
// SDR staring at "reconnecting" for the full call-hold ceiling. The cap re-arms
// once a socket proves itself, so it only has to reach the first server frame —
// but that stretch includes up to 2s of retry backoff plus a token mint and the
// handshake, so it must comfortably outlast a worst-case first attempt.
const POOL_HOLD_BAIL_MS = 6_000;

/**
 * Should this close hold the POOL seat and try to restore, rather than drop to
 * the picker? Keys on "not deliberate" (code !== 1000), matching
 * shouldHoldCall: production severs deliver 1006, never a specific code.
 */
export function shouldHoldPool(args: {
  code: number;
  inPool: boolean;
  reconnecting: boolean;
}): boolean {
  if (args.reconnecting) return false; // the existing migration guard owns it
  if (!args.inPool) return false; // the picker has nothing to hold
  return args.code !== 1000;
}

/**
 * How long the restore deadline should run, given when the current hold began.
 *
 * Each re-arm gets a full RESTORE_TIMEOUT_MS so a fresh socket's welcome + snapshot
 * round-trips aren't charged for the previous attempt's jitter, bounded by
 * HOLD_CEILING_MS from the drop.
 */
export function restoreDeadlineMs(holdStartedAt: number, now: number): number {
  return Math.max(0, Math.min(RESTORE_TIMEOUT_MS, holdStartedAt + HOLD_CEILING_MS - now));
}

export function useCockpit(impersonateSdr: string | null): CockpitController {
  const [state, dispatch] = useReducer(cockpitReducer, initialState);
  const [wsStatus, setWsStatus] = useState<WsStatus>('connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const deployingTtlRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // In-band link health: one ledger per mount, surviving reconnects (its
  // reconnectCount only means something across sockets). Fed by the connect
  // effect (pings sent / pongs received), read by the 1Hz sweep below.
  const linkLedgerRef = useRef<LinkHealthLedger | null>(null);
  linkLedgerRef.current ??= new LinkHealthLedger(PING_MS);
  const onlineRef = useRef(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [linkHealth, setLinkHealth] = useState<BffHealth>(() =>
    linkLedgerRef.current!.read(onlineRef.current),
  );
  const [singleWs, setSingleWs] = useState<boolean | null>(null);
  const [emailCheck, setEmailCheck] = useState(false);

  // 1Hz: sweep missed pings and publish a fresh snapshot (the same cadence
  // useBffPing updates at, so the route's render rate is unchanged). The browser
  // online/offline events feed the `offline` quality bucket, as in useBffPing.
  useEffect(() => {
    const on = (): void => {
      onlineRef.current = true;
    };
    const off = (): void => {
      onlineRef.current = false;
    };
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    const tick = setInterval(() => {
      const ledger = linkLedgerRef.current;
      if (!ledger) return;
      ledger.sweep();
      setLinkHealth(ledger.read(onlineRef.current));
    }, 1000);
    return () => {
      clearInterval(tick);
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  // True from a deploy `reconnect` nudge until the new socket restores (or fails):
  // tells `onclose` to migrate WITHOUT tearing down the live call — the migration
  // path suppresses the `ws_closed` dispatch so the phase never leaves `on_call` (the
  // Twilio effect is keyed on phase) — and to jitter the retry.
  const reconnectingRef = useRef(false);
  // Disposition frames clicked while the link is down — flushed on reconnect so a
  // call isn't left un-dispositioned (and the SDR un-stuck) by a transient drop.
  // Only `end_call` is queued: it's a settled fact about a finished call, safe to
  // replay. Pool join/leave are NOT queued — replaying them after a reconnect
  // could act on stale intent; those are simply re-clickable once online.
  const pendingSendsRef = useRef<CockpitClientFrame[]>([]);

  // Latest state in a ref so the (unmemoised) action callbacks read fresh
  // activePoolId / currentCall without being re-created on every render.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Synchronous "leave already sent" latch: `dispatch` is async, so two clicks in
  // one render tick both see leaveStatus === 'none' via stateRef and would each
  // send a leave_pool frame. This ref blocks the second one. Reset whenever the
  // leave settles back to 'none' (pool_left / ws_closed clear leaveStatus).
  const leaveSentRef = useRef(false);
  if (state.leaveStatus === 'none') leaveSentRef.current = false;

  useEffect(() => {
    dispatch({ type: 'sign_in' });
    let closed = false; // set on unmount so a late onclose doesn't reconnect
    let retry: ReturnType<typeof setTimeout> | undefined;
    // Reset only when a socket proves itself (`sawServerFrame`), never on `onopen`.
    let retryAttempt = 0;
    // For the ledger's reconnect count: any open after the first is a reconnect.
    let everOpened = false;
    let authRefusals = 0;
    let firstRefusalAt: number | null = null;
    // Via the ref: `onclose` closes over the effect's render, so `state` goes stale.
    const hasLiveCall = (): boolean => stateRef.current.currentCall != null;
    const inPool = (): boolean => stateRef.current.activePoolId != null;
    // Cockpit-socket keepalive (armed in ws.onopen, cleared in ws.onclose + on
    // unmount). The ping counter persists across reconnects within this effect run.
    let keepalive: ReturnType<typeof setInterval> | undefined;
    let keepaliveN = 0;
    // Armed after a deploy reconnect opens the new socket; fires `restore_failed` (→
    // picker) if `session_restored` never arrives. Cleared on restore-settled or unmount.
    let restoreTimer: ReturnType<typeof setTimeout> | undefined;
    // Armed on `session_restored`; clears the migration guard once the restored socket
    // has proven stable (see MIGRATION_SETTLE_MS). Cancelled if that socket closes first.
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    // Set true once a migration has definitively given up (restore deadline elapsed): a
    // late `session_restored` on the already-abandoned socket is then ignored, so the UI
    // can't flip picker → on_call after the call's been torn down. Reset on each connect.
    let gaveUp = false;
    // When the CURRENT hold began — the clock HOLD_CEILING_MS is measured against.
    let holdStartedAt: number | null = null;
    // The current hold protects only the pool seat (no live call): deadline
    // capped at POOL_HOLD_BAIL_MS.
    let poolOnlyHold = false;
    // `connect` awaits a token mint, so two attempts can overlap; only the latest installs.
    let connectSeq = 0;

    // The migration's final give-up. Re-armed when a replacement socket proves itself,
    // or the reconnect jitter and snapshot round-trips eat the window and trip a
    // premature give-up while a valid `session_restored` is inbound.
    const armRestoreDeadline = (): void => {
      if (restoreTimer) clearTimeout(restoreTimer);
      holdStartedAt ??= Date.now();
      let remaining = restoreDeadlineMs(holdStartedAt, Date.now());
      if (poolOnlyHold) {
        remaining = Math.min(remaining, POOL_HOLD_BAIL_MS);
      }
      restoreTimer = setTimeout(() => {
        gaveUp = true;
        reconnectingRef.current = false;
        holdStartedAt = null;
        poolOnlyHold = false;
        if (settleTimer) clearTimeout(settleTimer);
        logWs('restore-timeout', {}, 'warn');
        dispatch({ type: 'restore_failed' });
        // Giving up on the MIGRATION must not give up on the SOCKET, or the tab is
        // stranded with no link until a reload. But `onmessage` re-arms this deadline
        // once a replacement proves itself, so we reach here with a HEALTHY socket too —
        // reconnecting over that one orphans it (nothing closes it, its keepalive leaks).
        const live = wsRef.current;
        if (!live || live.readyState > WebSocket.OPEN) {
          if (retry) clearTimeout(retry);
          retry = setTimeout(() => void connect(), Math.random() * RECONNECT_MAX_MS);
        }
      }, remaining);
    };

    const connect = async (): Promise<void> => {
      const seq = ++connectSeq;
      gaveUp = false;
      setWsStatus('connecting');
      linkLedgerRef.current?.connecting();
      // Awaiting means the effect may have been torn down — or another connect started —
      // by the time we resume, so re-check before opening a socket nothing would clean up.
      const url = await getFreshWsUrl(impersonateSdr);
      if (closed || seq !== connectSeq) return;
      // Swap the ref BEFORE closing any predecessor, so its `onclose` sees itself
      // superseded and bows out instead of scheduling a second retry.
      const superseded = wsRef.current;
      if (keepalive) clearInterval(keepalive);
      const ws = new WebSocket(url);
      wsRef.current = ws;
      superseded?.close();

      // The BFF rejects a bad token INSIDE its ws `open` handler, so a doomed socket
      // fires `onopen` and only then closes 1008. Anything meaning "this connection is
      // good" therefore keys off the first server frame, not `onopen`.
      let sawServerFrame = false;

      ws.onopen = () => {
        logWs('open'); // url omitted — it carries the ?token= JWT
        // The BFF pushes `pools` on connect; ask again to be robust to races.
        ws.send(JSON.stringify(listPoolsFrame()));
        // Claim this socket as one a person can be expected to answer for. Per socket,
        // so it has to be re-claimed on every reconnect — and before any join, or the
        // seat is taken by a connection nothing can ask about.
        ws.send(JSON.stringify(presenceHelloFrame()));
        // The window is per socket: carrying it across a reconnect would leave a
        // working SDR unreported on the new one for up to a full window.
        lastBeatSentRef.current = null;
        // Keep this socket warm AND measured (see PING_MS). Unlike join/end/leave, a
        // LIVE CALL sends nothing on it for the whole call, so without this traffic
        // the edge idle-reaps the idle socket — and a cockpit-socket close is
        // destructive on both ends: the client clears the card (ws_closed → the
        // Twilio hangup effect) AND the BFF deregisters the SDR non-gracefully, so
        // the core HANGS UP the live call. Each pong now feeds the in-band ledger
        // (onmessage) instead of being discarded. First ping goes out immediately so
        // the readout gets an RTT within one round-trip of connecting.
        const ledger = linkLedgerRef.current;
        ledger?.opened(everOpened);
        everOpened = true;
        const ping = (): void => {
          if (ws.readyState !== WebSocket.OPEN) return;
          keepaliveN += 1;
          ledger?.pingSent(keepaliveN);
          ws.send(JSON.stringify(buildPing(keepaliveN)));
        };
        ping();
        keepalive = setInterval(ping, PING_MS);
      };

      ws.onmessage = (evt: MessageEvent) => {
        if (!sawServerFrame) {
          sawServerFrame = true;
          retryAttempt = 0;
          authRefusals = 0;
          firstRefusalAt = null;
          // Again, because the claim sent at `onopen` can arrive before the BFF has a
          // session to record it against and is dropped without a trace. Silently
          // losing it means joining as a seat nothing can ask about, and it is the one
          // frame with nothing downstream to retry it. Idempotent, so the ordinary
          // case just sets the same flag twice.
          ws.send(JSON.stringify(presenceHelloFrame()));
          // `send` writes straight to an OPEN socket, so a disposition clicked between
          // `onopen` and a 1008 close would vanish instead of queueing.
          setWsStatus('open');
          dispatch({ type: 'ws_open' });
          // Re-arm from NOW so the restore gets a full window, not what's left after the
          // reconnect jitter.
          if (reconnectingRef.current) armRestoreDeadline();
          // Flush any dispositions queued while the link was down, oldest first.
          const pending = pendingSendsRef.current;
          pendingSendsRef.current = [];
          for (const queued of pending) {
            logWs('flush', sendSummary(queued));
            ws.send(JSON.stringify(queued));
          }
        }
        // Pongs feed the in-band ledger (they used to be discarded); hello carries
        // the per-connection server config. Both are invisible to the reducer, so
        // they return before the cockpit frame parser exactly as they used to fall
        // through it as nulls.
        if (linkLedgerRef.current?.pongReceived(evt.data)) return;
        const hello = parseHello(evt.data);
        if (hello) {
          // Explicitly set on EVERY hello, false included: a rollback BFF that
          // stopped saying singleWs must flip reconnecting tabs back to the
          // two-socket world, not leave them stranded on a stale true.
          setSingleWs(hello.singleWs);
          setEmailCheck(hello.emailCheck);
          return;
        }
        const frame = parseCockpitFrame(evt.data);
        if (!frame) return; // ignore dialer_outcome / unknown
        logWs('recv', recvSummary(frame));
        switch (frame.kind) {
          case 'pools':
            // Pass the capability through PLAINLY — a conditional spread here
            // would latch a stale true forever against a flag-off server.
            dispatch({ type: 'pools', pools: frame.pools, seatHeld: frame.seatHeld });
            return;
          case 'voice_token':
            dispatch({ type: 'voice_token', voice: frame.voice });
            return;
          case 'pool_joined':
            dispatch({ type: 'pool_joined', poolId: frame.poolId });
            return;
          case 'lead_confirmed':
            dispatch({ type: 'lead_confirmed', callId: frame.callId });
            return;
          case 'lead_assigned':
            dispatch({ type: 'lead_assigned', call: frame.call });
            return;
          case 'talk_track':
            dispatch({
              type: 'talk_track',
              callId: frame.callId,
              name: frame.name,
              markdown: frame.markdown,
            });
            return;
          case 'lead_calendar':
            dispatch({
              type: 'lead_calendar',
              callId: frame.callId,
              status: frame.status,
              calendar: frame.calendar,
            });
            return;
          case 'call_ended':
            dispatch({ type: 'call_ended', callId: frame.callId });
            return;
          case 'pool_leaving':
            // The core is draining the SDR's pending calls — show the wait popup.
            // Reported because the drain is where a leave gets stuck: SDRs have been
            // found sitting in `leaving` with no dials, and the only prior evidence
            // was the SDR complaining.
            rumAction('cockpit.pool.draining', {
              pool_id: frame.poolId,
              pending_calls: frame.pendingCalls,
            });
            dispatch({ type: 'pool_leaving', pendingCalls: frame.pendingCalls, at: Date.now() });
            return;
          case 'presence_warning':
            rumAction('cockpit.presence.warned', { trigger: frame.trigger });
            dispatch({
              type: 'presence_warning',
              trigger: frame.trigger,
              expiresAt: frame.expiresAt,
            });
            return;
          case 'presence_clear':
            dispatch({ type: 'presence_clear' });
            return;
          case 'pool_left': {
            // Pairs with leave_requested. `waited_ms` is the SDR-visible drain time —
            // null when the ACK arrives with no local request behind it (reconnect
            // reconciliation), which is a different event and must not read as 0ms.
            const since = stateRef.current.leavingSince;
            rumAction('cockpit.pool.left', {
              pool_id: frame.poolId,
              waited_ms: since == null ? null : Date.now() - since,
              // Null for a leave the SDR asked for. Without it an exit nobody chose is
              // indistinguishable from one they did.
              reason: frame.reason ?? null,
            });
            dispatch({
              type: 'pool_left',
              poolId: frame.poolId,
              ...(frame.reason ? { reason: frame.reason } : {}),
              ...(frame.refusedReason != null ? { refusedReason: frame.refusedReason } : {}),
            });
            return;
          }
          case 'deploying':
            // The core paused dialing for a graceful deploy drain — show the banner.
            // Age it out after a bound: there is no retraction signal, and a socket
            // that survives the roll otherwise wears it until the next card.
            dispatch({ type: 'deploying' });
            if (deployingTtlRef.current != null) {
              clearTimeout(deployingTtlRef.current);
            }
            deployingTtlRef.current = setTimeout(() => {
              deployingTtlRef.current = null;
              dispatch({ type: 'deploying_timeout' });
            }, DEPLOYING_BANNER_TTL_MS);
            return;
          case 'session_restored':
            // A migration that already gave up (deadline elapsed → picker): don't flip
            // the UI back — but DO surrender the seat. The server restored the session
            // on this open socket and is heartbeating it, so a bare return leaves the
            // SDR seated and routable with no card in front of them.
            if (gaveUp) {
              logWs('restore-after-giveup', { poolId: frame.poolId }, 'warn');
              ws.send(JSON.stringify(leavePoolFrame(frame.poolId)));
              return;
            }
            // Reconnected to a task that still has our session — restore pool + call in
            // the UI now. But DON'T clear the migration guard yet: with lb_cookie
            // stickiness this may be the still-draining OLD task, which closes the socket
            // within its shutdown grace. Hold the guard (so `onclose` keeps migrating
            // instead of dropping the call) until the socket proves stable past that
            // grace; the deadline (restoreTimer) still bounds the whole migration.
            dispatch({
              type: 'session_restored',
              poolId: frame.poolId,
              currentCall: frame.currentCall,
            });
            // Lift the pool-only cap before re-arming, or the capped deadline
            // fires inside the settle window.
            if (poolOnlyHold) {
              poolOnlyHold = false;
              armRestoreDeadline();
            }
            if (settleTimer) clearTimeout(settleTimer);
            settleTimer = setTimeout(() => {
              reconnectingRef.current = false;
              holdStartedAt = null; // this hold is over; the next one starts its own clock
              poolOnlyHold = false;
              if (restoreTimer) {
                clearTimeout(restoreTimer);
                restoreTimer = undefined;
              }
            }, MIGRATION_SETTLE_MS);
            return;
          case 'reconnect':
            // The BFF task is deploying: migrate to the healthy new task WITHOUT tearing
            // down the live call. Detach + close this socket (its onclose is a superseded
            // no-op) and reconnect after jitter; the phase is preserved until
            // `session_restored` reconciles. The restore deadline is the single arbiter of
            // giving up: while reconnecting, `onclose` keeps RETRYING instead of dropping
            // the call, and if `session_restored` never lands the deadline drops us to the
            // picker.
            reconnectingRef.current = true;
            gaveUp = false; // starting a fresh migration
            holdStartedAt = null; // ...so it gets a full window, not the tail of an old one
            // A nudged migration keeps the full window even when idle: the
            // restore is expected to land.
            poolOnlyHold = false;
            logWs('reconnect-nudge', { reason: frame.reason });
            if (retry) clearTimeout(retry);
            if (keepalive) clearInterval(keepalive);
            if (settleTimer) clearTimeout(settleTimer);
            // Arm the migration-wide deadline now (bounds a socket that never opens);
            // ws.onopen re-arms it so welcome + restore gets a full window.
            armRestoreDeadline();
            setWsStatus('connecting');
            linkLedgerRef.current?.connecting();
            wsRef.current = null; // detach so this socket's onclose is a superseded no-op
            ws.close(1000, 'reconnecting');
            retry = setTimeout(connect, Math.random() * RECONNECT_JITTER_MS);
            return;
        }
      };

      ws.onclose = (evt: CloseEvent) => {
        if (closed) return;
        // A socket we've already migrated away from (a deploy `reconnect` detached it, or
        // the old task closed it after we moved on): not ours to act on. Guard BEFORE
        // touching `keepalive` — that var now points at the NEW socket's interval, so
        // clearing it here would silently leave the migrated socket un-pinged (→ idle-
        // reaped → the live call dropped a minute in).
        if (ws !== wsRef.current) return;
        if (keepalive) clearInterval(keepalive); // stop pinging this (now dead) socket
        // This socket closed before it settled — it was the draining old task. Cancel the
        // settle so the guard is NOT cleared; reconnectingRef stays set and we migrate on.
        if (settleTimer) {
          clearTimeout(settleTimer);
          settleTimer = undefined;
        }
        setWsStatus('closed');
        linkLedgerRef.current?.closed();
        // Cached, so drop it or every retry replays the same dead token.
        if (evt.code === 1008) {
          invalidateWsToken();
          authRefusals += 1;
          firstRefusalAt ??= Date.now();
          // A sustained run means the SESSION is gone, which re-minting can't fix and
          // nothing else re-checks. Never mid-call: the hold is already bounded by the
          // core's reap, so waiting is free and yanking an SDR out of a call is not.
          if (isAuthGenuinelyLost(authRefusals, firstRefusalAt, Date.now()) && !hasLiveCall()) {
            closed = true;
            logWs('auth-lost', { refusals: authRefusals }, 'error');
            redirectToLogin();
            return;
          }
        }
        // `ws_closed` clears the card, which hangs up the Twilio leg — so an unexpected
        // close mid-call takes the same migration path a deploy nudge uses, leaving the
        // restore deadline as the only thing that gives up.
        if (
          shouldHoldCall({
            code: evt.code,
            hasLiveCall: hasLiveCall(),
            reconnecting: reconnectingRef.current,
          })
        ) {
          reconnectingRef.current = true;
          armRestoreDeadline();
        } else if (
          shouldHoldPool({
            code: evt.code,
            inPool: inPool(),
            reconnecting: reconnectingRef.current,
          })
        ) {
          // Idle in a pool on an unexpected close: hold the seat and restore
          // rather than drop to the picker.
          reconnectingRef.current = true;
          poolOnlyHold = true;
          armRestoreDeadline();
        } else if (!reconnectingRef.current) {
          dispatch({ type: 'ws_closed' });
        }
        // 1000/1001 are clean closes; anything else is an unexpected drop.
        logWs(
          'close',
          { code: evt.code, wasClean: evt.wasClean },
          evt.code === 1000 || evt.code === 1001 ? 'info' : 'warn',
        );
        retryAttempt += 1;
        // A held call backs off on a tighter ceiling: the restore deadline is already
        // running, and every second of backoff is spent against it.
        const cap = reconnectingRef.current ? RECONNECT_HOLD_MAX_MS : RECONNECT_MAX_MS;
        retry = setTimeout(() => void connect(), backoffDelayMs(retryAttempt, RECONNECT_MS, cap));
      };

      ws.onerror = () => {
        logWs('error', {}, 'error');
        ws.close();
      };
    };

    void connect();

    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      if (keepalive) clearInterval(keepalive);
      if (restoreTimer) clearTimeout(restoreTimer);
      if (settleTimer) clearTimeout(settleTimer);
      reconnectingRef.current = false;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [impersonateSdr]);

  const send = (frame: CockpitClientFrame): void => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      logWs('send', sendSummary(frame));
      ws.send(JSON.stringify(frame));
    } else if (frame.kind === 'end_call' || frame.kind === 'silence_gate') {
      // Link down: hold it and flush when the socket reopens. end_call is the
      // disposition; silence_gate rides along because the deploy-reconnect path
      // keeps the live call running — a cut receipt dropped here would replay
      // the hangup later while permanently losing the row that separates a gate
      // cut from an SDR hangup (Codex P2 on #615). Queue order preserves
      // receipt-before-end_call, so the flush tells the story in sequence.
      logWs('queue', sendSummary(frame), 'warn');
      pendingSendsRef.current.push(frame);
    }
    // Other frames sent while offline are dropped (the UI gates them on wsStatus).
  };

  const selectPool = (poolId: string): void => {
    if (stateRef.current.phase !== 'pool_select') return;
    dispatch({ type: 'select_pool', poolId }); // blocks the UI until the ACK
    send(joinPoolFrame(poolId));
  };

  // The SDR finished a REAL conversation and ended it — the counterpart to
  // markVoicemail below, finally wired (D1: this function existed unreferenced while
  // every SDR-ended call was filed as a voicemail miss). Sends the event only, no
  // disposition (Decision 3); the core writes `sdr_hangup` and `ended_by: sdr`
  // becomes non-zero for the first time.
  const endCall = (source: HangupSource): void => {
    const call = stateRef.current.currentCall;
    if (!call || stateRef.current.phase !== 'on_call') return;
    rumAction('cockpit.call.ended', {
      call_id: call.callId,
      pool_id: call.poolId,
      confirmed: call.confirmed === true,
      source,
    });
    logCall('end_call', {
      event: 'cockpit_end_call',
      call_id: call.callId,
      pool_id: call.poolId,
      confirmed: call.confirmed === true,
      source,
    });
    dispatch({ type: 'end_call' });
    send(endCallFrame(call.callId));
  };

  const leavePool = (): void => {
    const { phase, activePoolId, leaveStatus } = stateRef.current;
    if (!activePoolId || leaveStatus !== 'none' || leaveSentRef.current) return;
    if (phase !== 'waiting' && phase !== 'on_call' && phase !== 'ending') return;
    leaveSentRef.current = true; // block a second synchronous click before re-render
    // SERVER-DRIVEN leave: ask to exit and let the core decide. It NEVER cuts off a
    // pending call — if any are in flight it drains them and replies `pool_leaving`
    // (→ the "wait for pending calls" popup), then `pool_left` when done; with
    // nothing in flight it replies `pool_left` at once. We do NOT change the phase
    // here (that would drive the Twilio hangup and cut a live call), so the leave is
    // reflected only by leaveStatus until pool_left.
    // Opens the leave lifecycle: this, then `draining` if calls are in flight, then
    // `left` or `leave_abandoned`. A request with no terminal partner is a stuck drain.
    rumAction('cockpit.pool.leave_requested', {
      pool_id: activePoolId,
      // A leave pressed mid-call drains; from `waiting` it should return at once.
      on_call: stateRef.current.currentCall != null,
    });
    dispatch({ type: 'request_leave', at: Date.now() });
    send(leavePoolFrame(activePoolId));
  };

  // The drain has run far longer than a leave ever should. Return to the picker so the
  // SDR can re-join (which is what makes the core release them) instead of sitting
  // behind an overlay until the core's 30-minute ceiling fires.
  //
  // Local dispatch FIRST, frame second: the drain may be stalled precisely because the
  // socket is dead, and the escape has to work anyway. The BFF reconciles on the next
  // join if the frame never lands.
  const abandonLeave = (): void => {
    const { activePoolId, leaveStatus, currentCall } = stateRef.current;
    if (!activePoolId || leaveStatus === 'none' || currentCall != null) return;
    // The SDR gave up waiting. This is the stuck-drain signal with a human behind it,
    // so it is worth more than any timer we could infer server-side.
    const since = stateRef.current.leavingSince;
    rumAction('cockpit.pool.leave_abandoned', {
      pool_id: activePoolId,
      leave_status: leaveStatus,
      waited_ms: since == null ? null : Date.now() - since,
    });
    dispatch({ type: 'abandon_leave' });
    send(abandonLeaveFrame(activePoolId));
  };

  const refreshVoiceToken = (): void => send(refreshVoiceTokenFrame());

  // Fire-and-forget telemetry for the banner. Guard on phase so a late fire after the
  // call already ended can't emit a stray frame.
  const reportVoicemailPromptRaised = (): void => {
    const call = stateRef.current.currentCall;
    if (!call || stateRef.current.phase !== 'on_call') return;
    send(voicemailPromptRaisedFrame(call.callId));
  };

  // Fire-and-forget receipts for the mutual-silence gate (cockpit/silence-gate.ts),
  // same phase guard as the voicemail banner. The RUM action marks the session
  // replay, so a support person can see the prompt in a recording.
  const reportSilenceGate = (event: SilenceGateReport): void => {
    const call = stateRef.current.currentCall;
    if (!call || stateRef.current.phase !== 'on_call') return;
    rumAction('cockpit.silence_gate', { call_id: call.callId, pool_id: call.poolId, ...event });
    send(silenceGateFrame(call.callId, event));
  };

  // Deliberately NOT phase-guarded like the voicemail banner above: a Twilio error typically
  // arrives as the leg is already tearing down, so requiring `on_call` would drop exactly the
  // signals worth having, and a stray frame costs far less than a silent misattribution.
  const reportClientError = (code: number | null): void => {
    const call = stateRef.current.currentCall;
    if (!call) return;
    send(clientErrorFrame(call.callId, code));
  };

  // SDR pressed "Drop Call" to end a live call as an AMD miss. `mark_voicemail` leaves
  // `on_call`, which drives the Twilio hangup via the phase effect in cockpit.tsx; the
  // BFF counts the miss and its call_ended returns us to waiting, same as a disposition.
  const markVoicemail = (source: HangupSource): void => {
    const call = stateRef.current.currentCall;
    if (!call || stateRef.current.phase !== 'on_call') return;
    // Counted as an AMD miss, so this must stay the machine-answer end and never the
    // catch-all one. `confirmed` is the check on that: a dropped call AMD had CONFIRMED
    // as human is either an AMD error or an SDR reaching for the wrong control, and
    // both matter.
    rumAction('cockpit.call.dropped', {
      call_id: call.callId,
      pool_id: call.poolId,
      confirmed: call.confirmed === true,
      source,
    });
    logCall('call_dropped', {
      event: 'cockpit_call_dropped',
      call_id: call.callId,
      pool_id: call.poolId,
      confirmed: call.confirmed === true,
      source,
    });
    dispatch({ type: 'mark_voicemail' });
    send(callManuallyDroppedFrame(call.callId));
  };

  // Poll the BFF to re-push the card when audio is up but none arrived. Stable identity
  // is load-bearing: the cockpit.tsx reconcile interval depends on it, and Twilio
  // quality/RTT events re-render sub-2s during a call — an unstable ref would tear the
  // interval down before it fires (silent no-op). Hence useCallback with empty deps
  // reading wsRef, NOT the unmemoised `send`. Unqueued when offline; the next tick retries.
  const resyncCall = useCallback((): void => {
    const ws = wsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) return;
    logWs('send', { kind: 'resync_call' });
    ws.send(JSON.stringify(resyncCallFrame()));
  }, []);

  // Report a person, at most once a window. Stable identity and a ref-read socket for
  // the same reason resyncCall has them: this is wired to listeners that must not be
  // torn down and re-armed on every render.
  const lastBeatSentRef = useRef<number | null>(null);
  const reportActivity = useCallback((): void => {
    const ws = wsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (!shouldEmit(lastBeatSentRef.current, now)) return;
    lastBeatSentRef.current = now;
    ws.send(JSON.stringify(presenceBeatFrame()));
  }, []);

  const answerPresence = useCallback((): void => {
    const ws = wsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) return;
    lastBeatSentRef.current = Date.now();
    rumAction('cockpit.presence.answered', {});
    ws.send(JSON.stringify(presenceAckFrame()));
  }, []);

  return {
    state,
    wsStatus,
    linkHealth,
    singleWs,
    emailCheck,
    reportActivity,
    answerPresence,
    selectPool,
    endCall,
    leavePool,
    abandonLeave,
    refreshVoiceToken,
    reportVoicemailPromptRaised,
    reportSilenceGate,
    reportClientError,
    markVoicemail,
    resyncCall,
  };
}
